import iassign from 'immutable-assign'
import moment from 'moment'

import { Booking, BookingMap, BookingStep } from '../../../models/BookingAppointment'
import {
    isBasicPhoneNumberWithWarning,
    isEmail,
    isNotEmpty,
    isRequired,
    validate,
} from '../../shared/form-validator/validator'

import {
    AppointmentSetSocketConnectionStatus,
    AppointmentSubscribeToChannel,
    AppointmentUnsubscribeFromChannel,
    ReceiveAppointment,
    ReceiveExistingPatients,
    ResetAppointmentForm,
    ResetSearchResults,
    SelectAppointmentDay,
    SelectExistingPatient,
    SetAppointmentBookingStep,
    SetAppointmentDatetime,
    SetAppointmentPickerError,
    SetBookingError,
    SetNewPatientFromSearch,
    SetPending,
    SetSearchExistingPatientsPending,
    ToggleDirectSchedulingModal,
    UpdateFormFields,
    UpdateSearchFormFields,
} from './actions'

const ERROR_CANNOT_CONNECT = 'We couldn’t connect to the practice calendar.'
const tomorrow = moment().add(1, 'days')

export type DirectSchedulingState = BookingMap

function normalizeUSPhoneToE164(value: string | undefined): string {
    if (!value) {
        return ''
    }

    // If format is already E.164, keep it.
    if (value.trim().startsWith('+')) {
        return value.trim()
    }

    const digits = value.replace(/\D/g, '')
    if (digits.length === 10) {
        return `+1${digits}`
    }
    if (digits.length === 11 && digits.startsWith('1')) {
        return `+${digits}`
    }

    return value
}

type DirectSchedulingAction =
    | ReceiveAppointment
    | ToggleDirectSchedulingModal
    | UpdateFormFields
    | SetAppointmentBookingStep
    | SetPending
    | SetAppointmentDatetime
    | ResetAppointmentForm
    | SelectAppointmentDay
    | SetAppointmentPickerError
    | SetBookingError
    | UpdateSearchFormFields
    | SetSearchExistingPatientsPending
    | ReceiveExistingPatients
    | SelectExistingPatient
    | ResetSearchResults
    | SetNewPatientFromSearch
    | AppointmentSetSocketConnectionStatus
    | AppointmentSubscribeToChannel
    | AppointmentUnsubscribeFromChannel

const initialState: DirectSchedulingState = {}

const initialBooking = (chat: Models.ChatMetadata): Booking => {
    return {
        open: false,
        chat,
        isAdditionalPatientBooking: false,
        isNewPatient: false,
        socketConnected: false,
        searchPatients: {
            formElements: {
                firstName: {
                    value: '',
                    validators: [isRequired()],
                    isValid: false,
                    isDirty: false,
                },
                lastName: {
                    value: '',
                    validators: [isRequired()],
                    isValid: false,
                    isDirty: false,
                },
                dateOfBirth: {
                    value: '',
                    validators: [isRequired()],
                    isValid: false,
                    isDirty: false,
                },
                location: {
                    value: '',
                    validators: [isRequired()],
                    isValid: false,
                    isDirty: false,
                },
            },
            isFormValid: false,
            isFormDirty: false,
            isPending: false,
            patients: [],
        },
        patient: {
            formElements: {
                mobilePhone: {
                    value: '',
                    validators: [isBasicPhoneNumberWithWarning({ countryCode: 'US' })],
                    isValid: false,
                    isDirty: false,
                },
                email: {
                    value: '',
                    validators: [isEmail()],
                    isValid: false,
                    isDirty: false,
                },
                gender: {
                    value: '',
                    validators: [],
                    isValid: true,
                    isDirty: false,
                },
                procedure: {
                    value: '',
                    validators: [isNotEmpty()],
                    isValid: false,
                    isDirty: false,
                },
                location: {
                    value: '',
                    validators: [isNotEmpty()],
                    isValid: false,
                    isDirty: false,
                },
                provider: {
                    value: '',
                    validators: [],
                    isValid: true,
                    isDirty: false,
                },
            },
            isFormValid: false,
            isFormDirty: false,
            isPending: false,
            bookingStep: BookingStep.SEARCH,
            appointmentPicker: {
                selectedDate: tomorrow,
            },
        },
    }
}

export function reducer(state: DirectSchedulingState = initialState, action: DirectSchedulingAction) {
    switch (action.type) {
        case 'TOGGLE_DIRECT_SCHEDULING_MODAL': {
            return iassign(
                state,
                next => next,
                nextBookings => {
                    if (!nextBookings[action.chat.id]) {
                        nextBookings[action.chat.id] = initialBooking(action.chat)
                    }
                    nextBookings[action.chat.id].open = action.isOpened
                    return nextBookings
                },
            )
        }
        case 'UPDATE_FORM_FIELDS': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id].patient
                },
                nextForm => {
                    nextForm.formElements = action.formElements
                    nextForm.isFormDirty = true
                    nextForm.isFormValid = validate(nextForm.formElements)
                    return nextForm
                },
            )
        }
        case 'SET_APPOINTMENT_BOOKING_STEP': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id].patient
                },
                nextTab => {
                    if (nextTab.bookingStep === BookingStep.SHARED && action.bookingStep === BookingStep.BOOKED) {
                        return nextTab
                    }
                    nextTab.bookingStep = action.bookingStep

                    return nextTab
                },
            )
        }
        case 'SET_BOOKING_PENDING': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id].patient
                },
                nextTab => {
                    nextTab.isPending = action.isPending
                    return nextTab
                },
            )
        }
        case 'SELECT_APPOINTMENT_DAY': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id].patient.appointmentPicker
                },
                nextAppointmentPicker => {
                    nextAppointmentPicker.selectedDate = action.day
                    nextAppointmentPicker.selectedDatetime = undefined
                    return nextAppointmentPicker
                },
            )
        }
        case 'SET_APPOINTMENT_DATETIME': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id].patient.appointmentPicker
                },
                nextAppointmentPicker => {
                    nextAppointmentPicker.selectedDatetime = action.datetime
                    return nextAppointmentPicker
                },
            )
        }
        case 'RECEIVE_APPOINTMENT': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id]
                },
                nextBooking => {
                    if (action.appointment) {
                        nextBooking.appointment = action.appointment
                        nextBooking.patient.errorMessage = undefined
                    } else {
                        nextBooking.appointment = undefined
                        nextBooking.patient.errorMessage = ERROR_CANNOT_CONNECT
                    }

                    return nextBooking
                },
            )
        }

        case 'SET_BOOKING_ERROR': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id]
                },
                nextBooking => {
                    nextBooking.patient.errorMessage = action.message

                    return nextBooking
                },
            )
        }
        case 'RESET_APPOINTMENT_FORM': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id]
                },
                nextChat => {
                    const locationValue = nextChat.patient.formElements.location.value
                    nextChat = initialBooking(action.chat)
                    if (action.isAdditionalPatientBooking) {
                        ;[nextChat.patient.formElements, nextChat.searchPatients.formElements].forEach(form => {
                            form.location.value = locationValue
                            form.location.isDirty = true
                            form.location.isValid = true
                        })
                        nextChat.isAdditionalPatientBooking = action.isAdditionalPatientBooking
                    }
                    return nextChat
                },
            )
        }
        case 'SET_APPOINTMENT_PICKER_ERROR': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id].patient.appointmentPicker
                },
                nextAppointmentPicker => {
                    nextAppointmentPicker.errorMessage = action.errorMessage
                    return nextAppointmentPicker
                },
            )
        }
        case 'UPDATE_SEARCH_FORM_FIELDS': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id].searchPatients
                },
                nextForm => {
                    nextForm.formElements = action.formElements
                    nextForm.isFormDirty = true
                    nextForm.isFormValid = validate(nextForm.formElements)
                    return nextForm
                },
            )
        }
        case 'SET_SEARCH_EXISTING_PATIENTS_PENDING': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id].searchPatients
                },
                nextForm => {
                    nextForm.isPending = action.isPending
                    nextForm.selectedPatient = undefined
                    return nextForm
                },
            )
        }
        case 'RECEIVE_EXISTING_PATIENTS': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id].searchPatients
                },
                nextForm => {
                    nextForm.patients = action.patients
                    nextForm.isFormDirty = false
                    return nextForm
                },
            )
        }
        case 'SELECT_EXISTING_PATIENT': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id]
                },
                nextBooking => {
                    nextBooking.searchPatients.selectedPatient = action.patient
                    nextBooking.patient.formElements.email.value = action.patient.emailAddress
                    nextBooking.patient.formElements.mobilePhone.value = normalizeUSPhoneToE164(
                        action.patient.mobilePhone,
                    )
                    return nextBooking
                },
            )
        }
        case 'RESET_SEARCH_RESULTS': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id]
                },
                next => {
                    next.searchPatients.selectedPatient = undefined
                    next.searchPatients.patients = []
                    next.searchPatients.isFormDirty = true
                    next.patient.bookingStep = BookingStep.SEARCH
                    return next
                },
            )
        }

        case 'APPOINTMENT_CHANNEL_SUBSCRIBE': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id].channelId
                },
                () => action.transactionId,
            )
        }

        case 'APPOINTMENT_CHANNEL_UNSUBSCRIBE': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id].channelId
                },
                () => '',
            )
        }

        case 'APPOINTMENT_SET_SOCKET_CONNECTION_STATUS': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id].socketConnected
                },
                () => action.isConnected,
            )
        }
        case 'SET_NEW_PATIENT_FROM_SEARCH': {
            return iassign(
                state,
                next => {
                    return next[action.chat.id]
                },
                nextBooking => {
                    const { patientSearchCriteria } = action
                    nextBooking.isNewPatient = true
                    nextBooking.searchPatients.selectedPatient = {
                        id: '',
                        firstName: patientSearchCriteria.firstName,
                        lastName: patientSearchCriteria.lastName,
                        birthDate: patientSearchCriteria.dateOfBirth,
                        mobilePhone: '',
                        emailAddress: '',
                    }
                    nextBooking.patient.formElements.location.value = patientSearchCriteria.locationId
                    nextBooking.patient.formElements.location.isDirty = true
                    nextBooking.patient.formElements.location.isValid = true
                    nextBooking.patient.bookingStep = BookingStep.NONE
                    return nextBooking
                },
            )
        }
        default:
            return state
    }
}
